import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminCurrencies.module.css';
import axiosClient from '../../api/axiosClient';

interface Currency {
  currency_code: string;
  name: string;
  symbol: string;
  rate_vs_usd: number;
  display_order: number;
  enabled: boolean;
  updated_at: string;
}

const BLANK = { code: '', name: '', symbol: '', rateVsUsd: '', displayOrder: '0' };

const AdminCurrencies: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, { rate: string; symbol: string; name: string }>>({});
  const [newForm, setNewForm] = useState(BLANK);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    axiosClient.get('/admin/currencies').then(({ data }) => {
      setCurrencies(data.currencies);
      const d: Record<string, { rate: string; symbol: string; name: string }> = {};
      data.currencies.forEach((c: Currency) => {
        d[c.currency_code] = { rate: String(c.rate_vs_usd), symbol: c.symbol, name: c.name };
      });
      setDrafts(d);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveCurrency = async (code: string) => {
    const draft = drafts[code];
    if (!draft) return;
    try {
      await axiosClient.patch(`/admin/currencies/${code}`, {
        rateVsUsd: draft.rate, symbol: draft.symbol, name: draft.name,
      });
      toast.success(`${code} updated.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save.');
    }
  };

  const toggleEnabled = async (code: string, enabled: boolean) => {
    try {
      await axiosClient.patch(`/admin/currencies/${code}`, { enabled });
      toast.success(`${code} ${enabled ? 'enabled' : 'disabled'}.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update.');
    }
  };

  const deleteCurrency = async (code: string) => {
    if (!window.confirm(`Remove ${code} from supported currencies?`)) return;
    try {
      await axiosClient.delete(`/admin/currencies/${code}`);
      toast.success(`${code} removed.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not remove.');
    }
  };

  const addCurrency = async () => {
    if (!newForm.code.trim() || !newForm.name.trim() || !newForm.symbol.trim() || !newForm.rateVsUsd.trim()) {
      toast.error('Fill in code, name, symbol, and rate.'); return;
    }
    try {
      await axiosClient.post('/admin/currencies', {
        code: newForm.code.trim(), name: newForm.name.trim(), symbol: newForm.symbol.trim(),
        rateVsUsd: newForm.rateVsUsd, displayOrder: newForm.displayOrder,
      });
      toast.success(`${newForm.code.toUpperCase()} added.`);
      setNewForm(BLANK);
      setShowNew(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not add currency.');
    }
  };

  return (
    <AdminShell>
      <PageHeader
        title="Currency Management"
        subtitle="Set which currencies your platform supports and their exchange rate — manually controlled, no external live-rate API."
        action={<AdminWalletBadge />}
      />
      <div className={panelStyles.page}>
        <button className={styles.addBtn} onClick={() => setShowNew((v) => !v)}><FiPlus /> Add Currency</button>

        {showNew && (
          <div className={styles.newRow}>
            <input placeholder="Code (e.g. GBP)" value={newForm.code} onChange={(e) => setNewForm({ ...newForm, code: e.target.value.toUpperCase() })} maxLength={6} />
            <input placeholder="Name (e.g. British Pound)" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} />
            <input placeholder="Symbol (e.g. £)" value={newForm.symbol} onChange={(e) => setNewForm({ ...newForm, symbol: e.target.value })} />
            <input placeholder="Rate vs 1 USD" type="number" step="0.0001" value={newForm.rateVsUsd} onChange={(e) => setNewForm({ ...newForm, rateVsUsd: e.target.value })} />
            <button className={styles.saveBtn} onClick={addCurrency}>Add</button>
          </div>
        )}

        {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}

        {!loading && (
          <div className={styles.table}>
            <div className={styles.headRow}>
              <span>Code</span><span>Name</span><span>Symbol</span><span>Rate vs 1 USD</span><span>Enabled</span><span></span>
            </div>
            {currencies.map((c) => {
              const draft = drafts[c.currency_code] || { rate: '', symbol: '', name: '' };
              const isUsd = c.currency_code === 'USD';
              return (
                <div key={c.currency_code} className={styles.row}>
                  <b>{c.currency_code}</b>
                  <input
                    value={draft.name}
                    onChange={(e) => setDrafts({ ...drafts, [c.currency_code]: { ...draft, name: e.target.value } })}
                  />
                  <input
                    value={draft.symbol}
                    onChange={(e) => setDrafts({ ...drafts, [c.currency_code]: { ...draft, symbol: e.target.value } })}
                  />
                  <input
                    type="number" step="0.0001"
                    value={draft.rate}
                    disabled={isUsd}
                    onChange={(e) => setDrafts({ ...drafts, [c.currency_code]: { ...draft, rate: e.target.value } })}
                  />
                  <label className={styles.toggle}>
                    <input type="checkbox" checked={c.enabled} disabled={isUsd} onChange={(e) => toggleEnabled(c.currency_code, e.target.checked)} />
                  </label>
                  <div className={styles.rowActions}>
                    <button onClick={() => saveCurrency(c.currency_code)} title="Save"><FiSave /></button>
                    {!isUsd && <button onClick={() => deleteCurrency(c.currency_code)} title="Remove"><FiTrash2 /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className={styles.hint}>
          USD is the fixed base currency (rate = 1) and can't be removed. Every other currency's rate is
          "how many units of that currency equal 1 USD" — e.g. INR at 83.5 means $1 = ₹83.5. Only enabled
          currencies show up in the currency selector across the whole platform.
        </p>
      </div>
    </AdminShell>
  );
};

export default AdminCurrencies;
